<template>
	<Row class="page-container-row">
		<Col :span="24">
			<PageContainer>
				<TypographyTitle :level="2" class="flex-grow">{{ $t('events.header') }}</TypographyTitle>
				<div class="flex-wrapper pd-vertical">
					<Space class="flex-grow" size="large">
						<Button
							type="primary"
							:disabled="!archiveButtonEnabled"
							:loading="archiveButtonLoading"
							@click="onArchiveClick"
						>
							{{ archiveButtonLabel }}
						</Button>
						<LabeledSwitch
							v-model="showArchived"
							:disabled="isLoading"
							:rightLabel="$t('events.showArchivedLabel')"
						/>
					</Space>
				</div>
				<div class="scrollable-x">
					<Table
						class="events-container"
						:columns="columns"
						:data-source="events"
						:pagination="{ defaultPageSize: 10, position: ['bottomLeft'] }"
						:loading="isLoading"
						:rowClassName="getRowClassName"
						:row-selection="{ selectedRowKeys: selectedRowKeys, onChange: onSelect }"
					>
						<template #headerCell="{ column }">
							<template v-if="column.dataIndex === 'trend'">
								<span class="trend-header" @click.stop>
									{{ column.title }}
									<RadioGroup
										v-model:value="trendPeriod"
										:options="trendPeriodOptions"
										option-type="button"
										button-style="solid"
										size="small"
									/>
								</span>
							</template>
						</template>
						<template #bodyCell="{ column, record }">
							<template v-if="column.dataIndex === 'source'">
								<NoLineBreaks>
									<AlertLevelDisplay :level="record.level" />
									<router-link class="clickable-source" :to="{ name: 'events-detail', params: { id: record.id } }">
										{{ $t('moduleStatuses.labels.' + record.source) }}
									</router-link>
									<TypographyText code v-if="record.subkey">{{ record.subkey }}</TypographyText>
								</NoLineBreaks>
								<pre class="last-message">{{ record.last_message }}</pre>
							</template>
							<template v-else-if="column.dataIndex === 'trend'">
								<EventTrendHistogram :key="record.id + '-' + trendPeriod" :record="record" :period="trendPeriod" />
							</template>
						</template>
					</Table>
				</div>
			</PageContainer>
		</Col>
	</Row>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { TypographyTitle, Table, Button, Row, Col, Space, TypographyText, RadioGroup } from 'ant-design-vue';
import { useI18n } from 'vue-i18n';

import ApiEvents from '@/api/ApiEvents';
import Consts from '@/api/Consts';

import { useEventsStore } from '@/stores/events';

import { datetimeToString } from '@/components/common/dateUtils';

import PageContainer from '@/components/common/PageContainer';
import NoLineBreaks from '@/components/common/NoLineBreaks';
import LabeledSwitch from '@/components/common/LabeledSwitch';
import AlertLevelDisplay from '@/components/common/AlertLevelDisplay';
import EventTrendHistogram from '@/components/events/EventTrendHistogram.vue';


const processEvents = item => ({
	...item,
	key: item.id,
	first_event: datetimeToString(item.first_event),
	last_event: datetimeToString(item.last_event),
});


export default {
	components: {
		TypographyTitle, Button, Row, Col, Table, Space, TypographyText,
		RadioGroup,
		PageContainer, NoLineBreaks, LabeledSwitch, AlertLevelDisplay, EventTrendHistogram,
	},
	setup() {
		const { t } = useI18n();
		const selectedRowKeys = ref([]);
		const eventsStore = useEventsStore();
		const archiveButtonLoading = ref(false);

		const events = computed(() => eventsStore.events.map(processEvents));
		const isLoading = computed(() => eventsStore.isLoading);

		const showArchived = computed({
			get: () => eventsStore.showArchived,
			set: value => {
				eventsStore.setShowArchived(value);
				eventsStore.refreshAll();
			},
		});
		const archiveButtonLabel = computed(() => t(showArchived.value ? 'events.restoreButtonLabel' : 'events.archiveButtonLabel'));
		const archiveButtonEnabled = computed(() => selectedRowKeys.value.length > 0);
		const getRowClassName = record => record.is_read ? '' : 'unread-event';

		const onSelect = newSelectedRowKeys => {
			selectedRowKeys.value = newSelectedRowKeys;
		};

		const onArchiveClick = () => {
			archiveButtonLoading.value = true;
			ApiEvents.eventGroupArchive(selectedRowKeys.value, !showArchived.value, () => {
				archiveButtonLoading.value = false;
				eventsStore.refreshAll();
			});
		};

		const trendPeriod = ref('2d');
		const trendPeriodOptions = computed(() => [
			{ value: '2d', label: t('events.trend.period2d') },
			{ value: '14d', label: t('events.trend.period14d') },
		]);
		const columns = computed(() => Consts.eventsTableColumnsComp.value.map(column => {
			if (column.dataIndex !== 'trend') {
				return column;
			}
			return {
				...column,
				trendPeriod: trendPeriod.value,
			};
		}));

		onMounted(() => {
			window.scrollTo(0, 0);
			eventsStore.refreshAll();
		});

		watch(events, () => {
			selectedRowKeys.value = [];
		});

		return {
			selectedRowKeys,
			eventsStore,
			archiveButtonLoading,
			columns,
			events,
			isLoading,
			showArchived,
			archiveButtonLabel,
			archiveButtonEnabled,
			getRowClassName,
			onSelect,
			onArchiveClick,
			trendPeriod,
			trendPeriodOptions,
		};
	},
}
</script>

<style>
.last-message {
	padding-top: 10px;
	margin-bottom: 0;
	font-family: inherit;
	white-space: break-spaces;
	word-break: break-all;
}

.unread-event .ant-table-cell {
	background-color: #fffaeb;
}

.ant-table-tbody>tr.unread-event.ant-table-row:hover>td,
.ant-table-tbody>tr.unread-event>td.ant-table-cell-row-hover {
	background-color: #f5f1e8;
}

.events-container .ant-pagination:first-child {
	position: absolute;
	inset-block-start: -59px;
	inset-inline-end: 0;
}

.clickable-source {
	margin-inline-end: 4px;
}

.events-container .trend-header {
	display: inline-flex;
	align-items: center;
	gap: 8px;
}
</style>
